import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ClipboardList, CheckCircle2, Play } from 'lucide-react';

export const PRIORITY_BADGE = (p) => (
  <Badge variant="outline" className={`text-[10px] ${p === 'HIGH' ? 'border-red-300 text-red-700' : p === 'MEDIUM' ? 'border-amber-300 text-amber-700' : 'border-border text-muted-foreground'}`}>
    {p}
  </Badge>
);

export function TaskRow({ t, onAction, busy }) {
  return (
    <div className="py-3 border-b last:border-0 flex flex-wrap items-start gap-2" data-testid={`task-${t.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-medium flex items-center gap-2">
          {t.title}
          {t.is_overdue && <Badge variant="destructive" className="text-[10px]" data-testid={`task-overdue-${t.id}`}>OVERDUE</Badge>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {t.participant_access === false ? 'Participant (access removed) · ' : t.participant_name ? `${t.participant_name} · ` : ''}
          {t.due_date ? `Due ${t.due_date}${t.due_time ? ` ${t.due_time}` : ''}` : 'No due date'}
          {t.assignee_name ? ` · ${t.assignee_name}` : ' · Unassigned'} · by {t.created_by_name || 'staff'}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {PRIORITY_BADGE(t.priority)}
        <Badge variant="secondary" className="text-[10px]">{t.status.replace('_', ' ')}</Badge>
        {onAction && t.status === 'OPEN' && (
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => onAction(t, 'start')} data-testid={`task-start-${t.id}`}><Play className="h-3 w-3 mr-1" />Start</Button>
        )}
        {onAction && ['OPEN', 'IN_PROGRESS'].includes(t.status) && (
          <Button size="sm" className="h-7" disabled={busy} onClick={() => onAction(t, 'complete')} data-testid={`task-complete-${t.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Complete</Button>
        )}
      </div>
    </div>
  );
}

export default function MyTasksPage() {
  const [tab, setTab] = useState('today');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: 20, search: search || undefined };
      if (tab === 'completed') params.status = 'DONE';
      const r = await api.get('/participant-tasks/my', { params });
      setData(r.data.data); setError(null);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'Participant tasks are not enabled for your organisation yet.' : 'Unable to load tasks');
      setData({ items: [], total: 0 });
    }
  }, [tab, search, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  let items = data?.items || [];
  if (tab === 'today') items = items.filter((t) => t.status !== 'DONE' && t.status !== 'CANCELLED' && (t.due_date === today || t.is_overdue));
  if (tab === 'upcoming') items = items.filter((t) => ['OPEN', 'IN_PROGRESS'].includes(t.status) && (!t.due_date || t.due_date > today));
  if (tab === 'overdue') items = items.filter((t) => t.is_overdue);

  const act = async (t, action) => {
    setBusy(true);
    try {
      await api.post(`/participant-tasks/${t.id}/${action}`);
      toast.success(action === 'complete' ? 'Task completed' : 'Task started');
      load();
      window.dispatchEvent(new Event('staff-v2-tasks-changed'));
    } catch (e) { toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="my-tasks-page">
      <div className="flex flex-wrap items-center gap-2">
        {['today', 'upcoming', 'overdue', 'completed'].map((k) => (
          <button key={k} onClick={() => { setTab(k); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize focus-visible:ring-2 focus-visible:ring-primary ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  data-testid={`tasks-tab-${k}`}>{k}</button>
        ))}
        <Input placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs ml-auto" data-testid="tasks-search" aria-label="Search tasks" />
      </div>
      <Card><CardContent className="py-2">
        {data === null && <div className="py-8 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>}
        {error && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tasks-unavailable">{error}</div>}
        {data && !error && items.length === 0 && (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="tasks-empty">
            <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-30" />No {tab} tasks.
          </div>
        )}
        {items.map((t) => <TaskRow key={t.id} t={t} onAction={act} busy={busy} />)}
      </CardContent></Card>
      {data && data.total > 20 && (
        <div className="flex justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page * 20 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
